<template>
  <div
    v-if="notice"
    class="wn"
    :class="`wn--${kind}`"
    :role="kind === 'ok' ? 'status' : 'alert'"
    data-test="workflow-notice"
    :data-kind="kind"
  >
    <!-- A confirmation: the short dark note, gone by itself. -->
    <template v-if="kind === 'ok'">
      <span class="wn__check"><WfIcon name="checkCircle" :size="18" :stroke="2" /></span>
      <span class="wn__line" data-test="workflow-notice-text">{{ okText }}</span>
    </template>

    <!-- A problem: a white card with a red or amber mark, one clear next step, and a close button. -->
    <template v-else>
      <span class="wn__mark"><WfIcon name="alert" :size="17" :stroke="2.1" /></span>
      <div class="wn__body">
        <div class="wn__title" data-test="workflow-notice-title">{{ notice.title }}</div>
        <div v-if="notice.text" class="wn__text" data-test="workflow-notice-text">{{ notice.text }}</div>
        <button
          v-if="notice.actionLabel"
          type="button"
          class="wn__action"
          data-test="workflow-notice-action"
          @click="$emit('action')"
        >{{ notice.actionLabel }}</button>
      </div>
      <button type="button" class="wn__close" aria-label="Close" title="Close" data-test="workflow-notice-close" @click="$emit('close')">
        <WfIcon name="x" :size="16" :stroke="2" />
      </button>
    </template>
  </div>
</template>

<script>
/**
 * The redesigned Workflow pages' notice (design "Workflows UI redesign", frames 1q / 1s; Workflow overhaul P6,
 * 2026-10-10). One at a time, bottom centre of the window, above the app chrome and the floating agent button.
 * It replaces the red toast slab for what happens OUT OF VIEW (a save or a delete that did not go through) and
 * the green one for a confirmation. Problems that the builder can show next to what caused them never come here.
 *
 *   notice = { kind: 'ok' | 'error' | 'warning', title, text, actionLabel }
 *
 *   ok       the short dark note with the teal check; its one line is `text` (or `title` when there is no text).
 *            Goes away by itself after 4 seconds: this component emits `close` when the time is up, and the page
 *            that owns the notice clears it. role="status".
 *   error    a card with a red mark, `title`, `text`, an optional action button (`actionLabel`) and a close button.
 *   warning  the same card with an amber mark.   Both stay until closed or acted on. role="alert".
 *
 * Presentational: no store, no router. The page decides what the action does (`action`) and when the notice is
 * gone (`close`, or a new notice replacing this one). A new `notice` object restarts the 4 seconds.
 *
 * ⛔ No prop named patNum / siteID / loading / allSettings / isSuperAdmin / userID (mixin-prop-shadow-guardrail).
 */
import WfIcon from './WfIcon.vue';

/** How long a confirmation stays (the design: "Confirmations leave after 4 seconds"). */
const OK_MS = 4000;

export default {
  name: 'WorkflowNotice',

  components: { WfIcon },

  props: {
    notice: { type: Object, default: null },
  },

  data() {
    return { timer: null };
  },

  computed: {
    /** Anything that is not ok or warning is drawn as an error: a loud mistake beats a silent one. */
    kind() {
      const k = this.notice && this.notice.kind;
      return k === 'ok' || k === 'warning' ? k : 'error';
    },

    okText() {
      return this.notice ? (this.notice.text || this.notice.title || '') : '';
    },
  },

  watch: {
    notice: {
      immediate: true,
      handler(next) {
        this.stopTimer();
        if (next && next.kind === 'ok') {
          this.timer = setTimeout(() => {
            this.timer = null;
            this.$emit('close');
          }, OK_MS);
        }
      },
    },
  },

  beforeDestroy() {
    this.stopTimer();
  },

  methods: {
    stopTimer() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './workflow-tokens';

// Fixed to the window, bottom centre. ⛔ z-order: above the settings sheet and the delete dialog, which are
// themselves above the floating agent button (z 9999), so a failed save is never hidden behind one of them.
.wn {
  @include wf-page-root;
  position: fixed;
  bottom: 24px;
  left: 50%;
  z-index: $wf-z-notice;
  box-sizing: border-box;
  transform: translateX(-50%);
  max-width: calc(100vw - 32px);

  &--ok {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    border-radius: 12px;
    background: $fu-neutral-950;
    color: #ffffff;
    font-size: 14px;
    line-height: 1.4;
    box-shadow: $fu-shadow-lg;
  }

  &--error,
  &--warning {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    width: 420px;
    padding: 14px 12px 14px 14px;
    border: 1px solid $fu-border;
    border-radius: 14px;
    background: $fu-surface;
    color: $fu-text;
    box-shadow: $fu-shadow-lg;
  }
}

.wn__check {
  display: flex;
  flex: none;
  color: $fu-brand-300;
}

.wn__line {
  min-width: 0;
  overflow-wrap: anywhere;
}

.wn__mark {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: $fu-danger-subtle;
  color: $fu-danger;

  .wn--warning & {
    background: $fu-warning-subtle;
    color: $fu-warning-text;
  }
}

.wn__body {
  flex: 1;
  min-width: 0;
}

.wn__title {
  padding-top: 5px;
  font-size: 14.5px;
  font-weight: 700;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.wn__text {
  margin-top: 3px;
  font-size: 13px;
  line-height: 1.45;
  color: $fu-neutral-600;
}

.wn__action {
  @include fu-btn(secondary, sm);
  margin-top: 10px;

  .wn--warning & { @include fu-btn(primary, sm); }
}

.wn__close {
  @include fu-icon-btn(30px);
}
</style>
